import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  AppRequestBooleanFilter,
  AppRequestDatePreset,
  defaultAppRequestsListFilter,
  isAppRequestSortProperty,
  SupplyAppsSortDirection,
  type AppRequestsListFilter,
} from './model';

export interface AppRequestsRouteSearch {
  search?: string;
  publishers?: string;
  platforms?: string;
  processed?: string;
  blocked?: string;
  connected?: string;
  bundleIds?: string;
  appStoreUrls?: string;
  date?: string;
  page?: number;
  size?: number;
  sortProperty?: string | null;
  sortDirection?: string;
}

export function appRequestsRouteSearchSchema(
  search: Record<string, unknown>,
): AppRequestsRouteSearch {
  const values = {
    search: optionalTrimmedString(search['search']),
    publishers: optionalString(search['publishers']),
    platforms: optionalString(search['platforms']),
    processed: optionalTrimmedString(search['processed']),
    blocked: optionalTrimmedString(search['blocked']),
    connected: optionalTrimmedString(search['connected']),
    bundleIds: optionalTrimmedString(search['bundleIds']),
    appStoreUrls: optionalTrimmedString(search['appStoreUrls']),
    date: optionalTrimmedString(search['date']),
    page: optionalPositiveInteger(search['page']),
    size: optionalPositiveInteger(search['size']),
    sortProperty:
      search['sortProperty'] === null ? null : optionalTrimmedString(search['sortProperty']),
    sortDirection: optionalTrimmedString(search['sortDirection']),
  };
  return Object.fromEntries(
    Object.entries(values).filter(
      (entry): entry is [string, string | number | null] => entry[1] !== undefined,
    ),
  );
}

export function parseAppRequestsRouteSearch(search: AppRequestsRouteSearch): AppRequestsListFilter {
  return {
    searchTerm: search.search ?? defaultAppRequestsListFilter.searchTerm,
    publisherIds: commaValues(search.publishers),
    platformIds: commaValues(search.platforms),
    processed: booleanFilter(search.processed),
    blocked: booleanFilter(search.blocked),
    connectedWithApp: booleanFilter(search.connected),
    bundleIds: search.bundleIds ?? defaultAppRequestsListFilter.bundleIds,
    appStoreUrls: search.appStoreUrls ?? defaultAppRequestsListFilter.appStoreUrls,
    datePreset:
      search.date === AppRequestDatePreset.Last7Days
        ? AppRequestDatePreset.Last7Days
        : AppRequestDatePreset.Today,
    page: search.page ? search.page - 1 : defaultAppRequestsListFilter.page,
    size: search.size ?? defaultAppRequestsListFilter.size,
    sortProperty:
      search.sortProperty === null ||
      (search.sortProperty && isAppRequestSortProperty(search.sortProperty))
        ? search.sortProperty
        : defaultAppRequestsListFilter.sortProperty,
    sortDirection:
      search.sortDirection === SupplyAppsSortDirection.Ascending ||
      search.sortDirection === SupplyAppsSortDirection.Descending
        ? search.sortDirection
        : defaultAppRequestsListFilter.sortDirection,
  };
}

export function serializeAppRequestsRouteSearch(
  filter: AppRequestsListFilter,
): AppRequestsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(filter.publisherIds.length ? { publishers: filter.publisherIds.join(',') } : {}),
    ...(filter.platformIds.length ? { platforms: filter.platformIds.join(',') } : {}),
    ...(filter.processed !== AppRequestBooleanFilter.All ? { processed: filter.processed } : {}),
    ...(filter.blocked !== AppRequestBooleanFilter.All ? { blocked: filter.blocked } : {}),
    ...(filter.connectedWithApp !== AppRequestBooleanFilter.All
      ? { connected: filter.connectedWithApp }
      : {}),
    ...(filter.bundleIds.trim() ? { bundleIds: filter.bundleIds.trim() } : {}),
    ...(filter.appStoreUrls.trim() ? { appStoreUrls: filter.appStoreUrls.trim() } : {}),
    ...(filter.datePreset !== defaultAppRequestsListFilter.datePreset
      ? { date: filter.datePreset }
      : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultAppRequestsListFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultAppRequestsListFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultAppRequestsListFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

export function countActiveAppRequestSecondaryFilters(filter: AppRequestsListFilter) {
  return [
    filter.publisherIds.length > 0,
    filter.platformIds.length > 0,
    filter.processed !== AppRequestBooleanFilter.All,
    filter.blocked !== AppRequestBooleanFilter.All,
    filter.connectedWithApp !== AppRequestBooleanFilter.All,
    Boolean(filter.bundleIds.trim()),
    Boolean(filter.appStoreUrls.trim()),
  ].filter(Boolean).length;
}

function optionalString(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function commaValues(value: string | undefined) {
  return value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
}

function booleanFilter(value: string | undefined) {
  if (value === AppRequestBooleanFilter.Yes) return AppRequestBooleanFilter.Yes;
  if (value === AppRequestBooleanFilter.No) return AppRequestBooleanFilter.No;
  return AppRequestBooleanFilter.All;
}
